import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp } from "lucide-react";
import { ExpiryRiskTimeline } from '@/gen/inventory/common_pb';

const expiryChartConfig = {
  total_items: {
    label: "Total Items",
    color: "#e5e7eb", // fallback gray
  },
};

function getExpiryColor(bucket: any) {
  let label = '';
  if (typeof bucket === 'string') {
    label = bucket;
  } else if (bucket && typeof bucket === 'object') {
    label = bucket.label || bucket.name || JSON.stringify(bucket);
  } else {
    label = String(bucket);
  }

  // Red to green gradient
  if (label.includes('0_1') || label.includes('0-1')) return '#b91c1c'; // red-700
  if (label.includes('1_3') || label.includes('1-3')) return '#f59e42'; // orange/yellow
  if (label.includes('3_6') || label.includes('3-6')) return '#fde047'; // yellow-300
  if (label.includes('6_12') || label.includes('6-12')) return '#22c55e'; // green-500
  if (label.includes('12')) return '#15803d'; // green-700
  return '#e5e7eb'; // Gray-200
}

interface ExpiryTimelineChartProps {
  expiryTimeline: ExpiryRiskTimeline[];
}

const ExpiryTimelineChart: React.FC<ExpiryTimelineChartProps> = ({ expiryTimeline }) => {
  return (
    <Card className="bg-[#FCFCFC] h-full">
      <div className="m-4 flex items-center text-gray-600">
        <TrendingUp className="w-4 h-4" />
        <p className="text-sm ml-1">Expiry Timeline</p>
      </div>
      <hr className="my-4" />
      <CardHeader>
        <CardTitle>Expiry Risk Timeline</CardTitle>
        <CardDescription>Expiring stock by timeline</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <ChartContainer config={expiryChartConfig} className="h-full w-full">
            <BarChart data={expiryTimeline}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="monthRange"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => {
                  let label = '';
                  if (typeof value === 'string') label = value;
                  else if (value && typeof value === 'object') label = value.label || value.name || JSON.stringify(value);
                  else label = String(value);
                  return label.slice(0, 10);
                }}
              />
              <YAxis
                dataKey="itemsAtRisk"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                padding={{ top: 25 }}
                tick={{ dx: -10 }}
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Bar dataKey="itemsAtRisk" radius={8}>
                {expiryTimeline.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={getExpiryColor(entry.monthRange)} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </ResponsiveContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="leading-none text-muted-foreground">
          Visualize your stock's expiry risk at a glance.
        </div>
      </CardFooter>
    </Card>
  );
};

export default ExpiryTimelineChart; 